<template>
  <div ref="main-content">
    <RecordsView
      class="records-view limits-usage-records"
      id="limits-usage-extras"
      :apiStatus="apiStatus"
      :referenceData="referenceData.extrasServicesRefData"
      :recordsData="extrasLimitsRecordData"
      :includeFilter="true"
      :filterData="filterData"
      :linkColumnValue="null"
      :defaultNoData="''"
      :haveMinHeight="false"
      :includeRecentHeader="false"
      :quickNavAlignment="quickNavLinkAlignment"
      :includeTopAlert="false"
      overrideContentClass="top-content"
      overrideContentId="extras-limits-table"
      @updatedFilterData="updateFilterData"
    >
      <template v-slot:customHeader>
        <h2>Extras</h2>
      </template>
      <template #paragraphData>
        <slot name="paragraphData" />
      </template>
      <template #filterHeader>
        <slot name="filterHeader" />
      </template>
      <template v-slot:resultsLabel>
        <p v-if="numOfFilterResults > 0 && !noPrimaryTableRecordsFound && productOnDate" class="results-label">
          {{ getResultsLabel(numOfFilterResults, productOnDate.name, appliedFilters.limits) }}
        </p>
      </template>
      <template v-slot:quickNavLinks>
        <QuickNavAnchorLinks :quickNavAnchorLinks="limitsAndUsageQuickNavLinks" @navigateTo="scrollTo" v-if="!noPrimaryTableRecordsFound" />
      </template>
      <template v-slot:additionalTables>
        <!-- Secondary limit table or Modality table -->
        <div v-if="modalityTable.showTable && !noPrimaryTableRecordsFound" ref="secondary-table">
          <RecordsView
            class="records-view limit-tables"
            id="secondary-table"
            :apiStatus="TABLE_API_STATUS"
            :referenceData="modalityTable.refData"
            :recordsData="modalityTable"
            :defaultNoData="''"
            :haveMinHeight="false"
            :linkColumnValue="null"
            :includeRecordHeader="false"
            :includeRecentHeader="false"
            overrideContentClass="table-content"
            overrideContentId=""
          >
            <template v-slot:quickNavLinks>
              <QuickNavAnchorLinks :quickNavAnchorLinks="returnToTopQuickNavLink" @navigateTo="scrollTo" />
            </template>
          </RecordsView>
        </div>
        <!-- Tertiary or Sub-limit table -->
        <div v-if="!noPrimaryTableRecordsFound" ref="tertiary-table">
          <div v-for="(subLimitData, key) in subLimitTable" :key="key">
            <RecordsView
              class="records-view limit-tables"
              id="tertiary-table"
              :apiStatus="TABLE_API_STATUS"
              :referenceData="subLimitData.refData"
              :recordsData="subLimitData"
              :defaultNoData="''"
              :haveMinHeight="false"
              :linkColumnValue="null"
              :includeRecordHeader="false"
              :includeRecentHeader="false"
              overrideContentClass="table-content"
              overrideContentId=""
              v-if="showSubLimitTable"
            >
              <template v-slot:quickNavLinks>
                <QuickNavAnchorLinks :quickNavAnchorLinks="returnToTopQuickNavLink" @navigateTo="scrollTo" />
              </template>
            </RecordsView>
          </div>
        </div>
        <!-- Replacement period table -->
        <div v-if="replacementTable.showTable && !noPrimaryTableRecordsFound" ref="replacement-period-table">
          <RecordsView
            class="records-view limit-tables"
            id="replacement-period-table"
            :apiStatus="TABLE_API_STATUS"
            :referenceData="referenceData.replacementPeriodRefData"
            :recordsData="replacementTable"
            :defaultNoData="''"
            :haveMinHeight="false"
            :linkColumnValue="null"
            :includeRecordHeader="false"
            :includeRecentHeader="false"
            :quickNavLinks="returnToTopQuickNavLink"
            overrideContentClass="table-content"
            overrideContentId=""
          >
            <template v-slot:icon>
              <svg>
                <use xlink:href="/assets/public/images/icons.svg#replacement-period" />
              </svg>
            </template>
            <template v-slot:quickNavLinks>
              <QuickNavAnchorLinks :quickNavAnchorLinks="returnToTopQuickNavLink" @navigateTo="scrollTo" />
            </template>
          </RecordsView>
        </div>
      </template>
    </RecordsView>
  </div>
</template>

<script>
import { RecordsView, QuickNavAnchorLinks } from '@/components';
import { ProductType, PolicyStatus } from '@general/js/enums';
import { FilterTypes, DeviceTypes, QuickNavLinksAlignment, AccessLevel } from '@/enums';
import {
  TABLE_API_STATUS,
  FILTER_HEADINGS,
  BLANK_COLUMNS,
  LIMIT_TABLE_COLUMNS,
  CONSUMPTION_TABLE_COLUMNS,
  TABLE_ALIGNMENTS,
  TWO_TABLE_ALIGNMENTS,
  SUB_LIMIT_COLUMNS,
  FILTER_OPTIONS_INDEX,
  REPLACEMENT_COLUMNS,
  DEFAULT_INFORMATION_TEXT,
  REPLACEMENT_PERIOD_COLUMN_WIDTHS,
  DEFAULT_LIMIT,
  DEFAULT_NO_RECORDS_TEXT,
  RETURN_TO_TOP_TEXT,
} from '@/constants/LimitsUsageTableConstants';
import { getCurrentDate, reverseFormatFormDate, formatFormDate, checkSomeObjectPropertiesTrue, isEmptyString, isDateAfter, isDateEqual } from '@general/js/utils';
import { isDateFormat, sortMembersByRelationship, getResultsLabel, sortByObjectProperty, findItemWithAttribute, isDateInRange } from '@/utils';
import LimitsUsageServices from '@/services/MyClaims/LimitsUsage';
import LimitsUsageMixin from './LimitsUsageMixin';
import { useVuelidate } from '@vuelidate/core';

export default {
  setup() { return { v$: useVuelidate() }; },

  name: 'ExtrasLimitsUsage',

  components: {
    RecordsView,
    QuickNavAnchorLinks,
  },

  mixins: [LimitsUsageMixin],

  props: {
    refDataApiStatus: {
      type: Object,
      required: true,
    },
    referenceData: {
      type: Object,
      required: true,
    },
    tableRefData: {
      type: Object,
      required: true,
    },
    currentDeviceLayout: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      apiStatus: {
        refData: {
          fail: false,
          loading: false,
        },
        recordData: {
          fail: false,
          loading: false,
        },
      },
      extrasLimitsRecordData: {
        headings: Object.values(LIMIT_TABLE_COLUMNS),
        data: [],
        colAlignments: TABLE_ALIGNMENTS,
        noRecordsText: DEFAULT_NO_RECORDS_TEXT,
      },
      filterData: {
        filterName: '',
        filterFunction: this.applyFilters,
        filterOptions: [],
        filterDropdownData: [],
        filterNoRecordsText: DEFAULT_NO_RECORDS_TEXT,
      },
      currentAsOfDate: reverseFormatFormDate(getCurrentDate()),
      previousAsOfDate: null,
      selectedFilter: {
        member: {},
        limits: '',
      },
      appliedFilters: {
        member: {},
        limits: DEFAULT_LIMIT,
      },
      memberList: null,
      limitsAndUsage: null,
      dropdownLimits: [],
      replacementPeriods: null,
      modalityTable: {
        headings: [],
        showTable: false,
        data: [],
        colAlignments: TABLE_ALIGNMENTS,
        noRecordsText: DEFAULT_NO_RECORDS_TEXT,
        refData: DEFAULT_INFORMATION_TEXT,
      },
      showSubLimitTable: false,
      subLimitTable: [],
      replacementTable: {
        headings: [],
        showTable: false,
        data: [],
        colAlignments: TWO_TABLE_ALIGNMENTS,
        noRecordsText: DEFAULT_NO_RECORDS_TEXT,
        colSetting: REPLACEMENT_PERIOD_COLUMN_WIDTHS,
        refData: DEFAULT_INFORMATION_TEXT,
      },
      limitsAndUsageQuickNavLinks: [],
      returnToTopQuickNavLink: [],
      productOnDate: null,
      DeviceTypes,
      TABLE_API_STATUS,
    };
  },

  computed: {
    selectedMemberLimits() {
      if (this.limitsAndUsage != null) {
        return (
          this.limitsAndUsage.find((limitAndUsageItem) => {
            // The second condition is to ensure the current Limits & usage are returned. The outdated limitsAndConsumption object has `memberWaitingPeriodEndDate` equal to null.
            return limitAndUsageItem.personCode === this.selectedFilter.member.value && limitAndUsageItem.memberWaitingPeriodEndDate !== null;
          })?.limits ?? []
        );
      }
      return null;
    },

    numOfFilterResults() {
      let totalResult = this.extrasLimitsRecordData.data.length;

      // Only add to the total number of services if certain table is showing
      if (this.modalityTable.showTable) {
        totalResult += this.modalityTable.data.length;
      }
      if (this.showSubLimitTable) {
        this.subLimitTable.forEach((subLimit) => {
          totalResult += subLimit.data.length;
        });
      }
      if (this.replacementTable.showTable) {
        totalResult += this.replacementTable.data.length;
      }

      return totalResult;
    },

    showSubLimitAndReplacementTable() {
      return this.showSubLimitTable && this.replacementTable.showTable;
    },

    showTableNavLinks() {
      return this.modalityTable.showTable && this.showSubLimitAndReplacementTable;
    },

    showReturnToTop() {
      return this.currentDeviceLayout === DeviceTypes.MOBILE && this.showSubLimitTable;
    },

    quickNavLinkAlignment() {
      // Align to the left if no records are found for the margins and if Medically Prescribed Devices filter is selected
      if (this.noPrimaryTableRecordsFound || this.showTableNavLinks) {
        return QuickNavLinksAlignment.LEFT;
      }
      return QuickNavLinksAlignment.RIGHT;
    },

    noPrimaryTableRecordsFound() {
      return this.extrasLimitsRecordData.data.length === 0;
    },
  },

  beforeMount() {
    this.apiStatus.refData = this.refDataApiStatus;
    this.setQuickNavLinks();
    this.fetchRecordsData();
  },

  watch: {
    refDataApiStatus: {
      handler(newData) {
        this.$nextTick(() => {
          this.apiStatus.refData = newData;
        });
      },
      deep: true,
    },
    currentDeviceLayout() {
      this.updateReturnTopNavLinks();
    },
  },

  // Need to create the mounted when it should load the data from initial state
  methods: {
    setQuickNavLinks() {
      // Primary table quick nav table links
      this.limitsAndUsageQuickNavLinks = [
        {
          displayName: 'View categories',
          linkTo: 'secondary-table',
          isVisible: this.modalityTable.showTable && !this.showSubLimitAndReplacementTable,
        },
        {
          displayName: 'View standard categories',
          linkTo: 'secondary-table',
          isVisible: this.showTableNavLinks,
        },
        {
          displayName: 'View non-comestic prosthesis',
          linkTo: 'tertiary-table',
          isVisible: this.showTableNavLinks,
        },
        {
          displayName: 'View categories claimable every 3 years',
          linkTo: 'replacement-period-table',
          isVisible: this.showTableNavLinks,
        },
      ];

      // Return to top quick nav table link
      this.returnToTopQuickNavLink = [
        {
          displayName: RETURN_TO_TOP_TEXT,
          linkTo: 'main-content',
          isVisible: this.showReturnToTop,
        },
      ];
    },

    updateQuickNavLinks() {
      // Update main table nav links visibility
      if (this.limitsAndUsageQuickNavLinks && this.limitsAndUsageQuickNavLinks.length > 0) {
        this.limitsAndUsageQuickNavLinks[0].isVisible = this.modalityTable.showTable && !this.showSubLimitAndReplacementTable;
        this.limitsAndUsageQuickNavLinks.slice(1).forEach((_, index) => {
          this.limitsAndUsageQuickNavLinks[index + 1].isVisible = this.showTableNavLinks;
        });
      }
      this.updateReturnTopNavLinks();
    },

    updateReturnTopNavLinks() {
      // Update the return to top nav links visibility
      if (this.returnToTopQuickNavLink && this.returnToTopQuickNavLink.length > 0) {
        this.returnToTopQuickNavLink[0].isVisible = this.showReturnToTop;
      }
    },

    updateFilterData(filterData) {
      // The filter has been updated, need to make sure that the selected filterResult is saved into the state.
      const memberFilterData = filterData.filterOptions[FILTER_OPTIONS_INDEX.MEMBER];

      // Determine if there is a way to get the dropdown changing when the member changes.
      this.selectedFilter.member = this.memberList.find((memberItem) => {
        return memberItem.value === memberFilterData.filterResult;
      });

      // Need to assign the selected limits
      const limitFilterData = filterData.filterOptions[FILTER_OPTIONS_INDEX.LIMIT];

      // For the selected limits, since the array is an array of string, the selected limit can be saved directly.
      this.selectedFilter.limits = limitFilterData.filterResult;

      // For the date entered, current as of date will need to be saved
      const dateFilterData = filterData.filterOptions[FILTER_OPTIONS_INDEX.DATE];

      this.currentAsOfDate = dateFilterData.filterResult;

      // Update also the validation value for currentAsOfDate
      dateFilterData.filterResultInvalid = this.v$.currentAsOfDate.$invalid;
    },

    async fetchRecordsData() {
      // Setup the calls to the service layer to fetch the required data.
      // Set all the loading of the data to true, since there will not be any caching of any data since discrete dates being stored would have too many possibilities.
      this.apiStatus.recordData.loading = true;
      this.apiStatus.recordData.fail = false;

      try {
        // Only the member list data will not need to be loaded if the member list information already exists.
        if (!this.memberList) {
          this.updateMemberDropdownList(this.memberHistory);
        }

        // The replacement periods will only need to be fetched once
        if (!this.replacementPeriods) {
          this.replacementPeriods = await LimitsUsageServices.retrieveReplacementPeriods();
        }

        // The limits and usage data will only need to be fetched from the API if and when the as of date has been updated
        if (this.currentAsOfDate !== this.previousAsOfDate) {
          // Ensure empty string won't be passed to retrieveLimitsAndUsage()
          if (this.currentAsOfDate === '') {
            this.currentAsOfDate = reverseFormatFormDate(getCurrentDate());
          }

          // takes member number & date with format 'YYYY-MM-DD'
          this.limitsAndUsage = await LimitsUsageServices.retrieveLimitsAndUsage(this.policyNumber, formatFormDate(this.currentAsOfDate));

          // Set up the data to be displayed
          this.updateLimitsDropdownList(this.selectedMemberLimits);
          this.formatRecordDataListLimit(this.selectedMemberLimits);

          // Get the product based on the as at date
          // Either extras or a package product
          this.productOnDate = this.findProductsOnGivenDate(this.memberHistory, this.selectedFilter.member.value, this.currentAsOfDate, [
            ProductType.EXTRAS_ONLY,
            ProductType.PACKAGE,
          ]);

          this.previousAsOfDate = this.currentAsOfDate;
        }

        this.updateFilterOptions();
      } catch (error) {
        this.apiStatus.recordData.fail = true;
        this.$emit('recordDataFail');

        logger.error(error.stack);
        logger.error(error.message ?? error);
      } finally {
        this.apiStatus.recordData.loading = false;
      }
    },

    updateMemberDropdownList(unformattedMemberList) {
      let sortedMemberList = [];
      // Need to massage the unformatted member list into something that is useable
      if (unformattedMemberList && Array.isArray(unformattedMemberList)) {
        const formattedMemberList = [];

        const asAtDateToCompare = isEmptyString(this.currentAsOfDate) ? reverseFormatFormDate(getCurrentDate()) : this.currentAsOfDate;
        // eslint-disable-next-line arrow-body-style
        unformattedMemberList.forEach((memberResponseItem) => {
          const memberHistoryAsAtDate = memberResponseItem.insurableHistory.find((memberHistoryItem) => {
            if (memberHistoryItem.endDate) {
              return isDateInRange(asAtDateToCompare, this.parseFormDate(memberHistoryItem.startDate), this.parseFormDate(memberHistoryItem.endDate));
            }
            return (
              isDateEqual(asAtDateToCompare, reverseFormatFormDate(memberHistoryItem.startDate)) || isDateAfter(asAtDateToCompare, reverseFormatFormDate(memberHistoryItem.startDate))
            );
          });

          if (memberHistoryAsAtDate && memberHistoryAsAtDate.status.toUpperCase() === PolicyStatus.ACTIVE){
              formattedMemberList.push({
              value: memberResponseItem.personCode,
              relationship: memberResponseItem.coveredOnMembership,
              keepPrivate: memberResponseItem.keepPrivate,
              name: memberResponseItem.name,
            });
          }
        });

        // Filter other members if self-access from member-selection endpoint (DOA check)
        const filteredMemberList = formattedMemberList.filter((memberItem) => {
          // If full-access and policy holder access and if member is not keepPrivate
          if ((this.accessLevel === AccessLevel.FULL_ACCESS || this.accessLevel === AccessLevel.POLICY_HOLDER_ACCESS) && !memberItem.keepPrivate) {
            return true;
          }
          // If self-access
          if (this.accessLevel === AccessLevel.SELF_ACCESS && this.currentMemberDetail.personCode === memberItem.value) {
            return true;
          }
          // If logged member has keepPrivate on, show their own details
          if (memberItem.keepPrivate && this.currentMemberDetail.personCode === memberItem.value) {
            return true;
          }
          return false;
        });

        sortedMemberList = sortMembersByRelationship(filteredMemberList, this.membershipInfo?.composition);
      }

      this.memberList = sortedMemberList;

      // Set the selected member
      const currentMemberData = sortedMemberList.find((memberItem) => {
        return memberItem.value === this.currentMemberDetail.personCode;
      });
      this.selectedFilter.member = currentMemberData ?? sortedMemberList[0]; // Default to first if member cannot be found in the memberlist
      this.appliedFilters.member = this.selectedFilter.member;
    },

    updateLimitsDropdownList(unformattedLimitList) {
      let formattedLimitList = [];

      // Need to get the unique LimitsUsageServices for the selected member
      if (unformattedLimitList && Array.isArray(unformattedLimitList)) {
        // Add default view to filter list
        formattedLimitList.push(DEFAULT_LIMIT);

        unformattedLimitList.forEach((limitListItem) => {
          // Only push the name into the array, since there are no other identifier besides the name.
          formattedLimitList.push(limitListItem.name);
        });

        // Sort formatted dropdown list from A-Z
        formattedLimitList = formattedLimitList.sort((a, b) => {
          return a > b ? 1 : -1;
        });
      }

      this.dropdownLimits = formattedLimitList;
    },

    formatRecordDataListLimit(unformattedDataList) {
      const formattedLimitData = [];

      // Format the record data for the limits only right now, since modalities in the data structure will be dependent on the limit filter selected
      if (unformattedDataList && Array.isArray(unformattedDataList)) {
        unformattedDataList.forEach((unformattedDataItem) => {
          const recordRow = {};
          // Need to check if the limit filter has selected all limits or the group
          if (this.selectedFilter.limits === '' || this.selectedFilter.limits === DEFAULT_LIMIT || this.selectedFilter.limits === unformattedDataItem.name) {
            recordRow[LIMIT_TABLE_COLUMNS.LIMIT] = unformattedDataItem.name;
            recordRow[LIMIT_TABLE_COLUMNS.START_DATE] = this.defaultValue(unformattedDataItem.startPeriod, true);
            recordRow[LIMIT_TABLE_COLUMNS.END_DATE] = this.defaultValue(unformattedDataItem.endPeriod, true);
            recordRow[LIMIT_TABLE_COLUMNS.ANNUAL] = this.defaultValue(unformattedDataItem.totalLimit, false, true, unformattedDataItem.unlimited);
            recordRow[LIMIT_TABLE_COLUMNS.CONSUMPTION] = this.defaultValue(unformattedDataItem.totalConsumed, false, true);
            recordRow[LIMIT_TABLE_COLUMNS.REMAINING] = this.defaultValue(unformattedDataItem.totalAvailable, false, true, unformattedDataItem.unlimited);

            formattedLimitData.push(recordRow);
          }
        });
      }

      // Set the record data
      // Sort limit record data from A-Z
      this.extrasLimitsRecordData.data = sortByObjectProperty(formattedLimitData, LIMIT_TABLE_COLUMNS.LIMIT, null, 'ascending');
    },

    updateFilterOptions() {
      this.filterData.filterOptions = [
        {
          filterHeading: FILTER_HEADINGS.DATE,
          filterType: FilterTypes.DATE_INPUT,
          filterPlaceholder: 'DD / MM / YYYY',
          filterResult: this.currentAsOfDate,
          filterResultInvalid: this.v$.currentAsOfDate.$invalid,
          filterInvalidMessage: 'Please enter a date using the format DD/MM/YYYY.',
        },
        {
          filterHeading: FILTER_HEADINGS.MEMBER,
          filterType: FilterTypes.DROPDOWN_INPUT,
          filterPlaceholder: null,
          filterDropdownData: this.memberList,
          filterResult: this.selectedFilter.member.value,
        },
        {
          filterHeading: FILTER_HEADINGS.LIMIT,
          filterType: FilterTypes.DROPDOWN_INPUT,
          filterPlaceholder: DEFAULT_LIMIT,
          filterDropdownData: this.dropdownLimits,
          filterResult: this.selectedFilter.limits,
        },
      ];
    },

    applyFilters() {
      // Check to determine if date is valid and whether data needs to be loaded because the date has changed
      if (!this.v$.currentAsOfDate.$invalid && this.currentAsOfDate !== this.previousAsOfDate) {
        // Wait for limit data to be updated before updating the sub tables' data
        this.fetchRecordsData().then(() => {
          this.updateSubTables();
        });
      } else {
        // Get the limits data that is for the selected member
        this.formatRecordDataListLimit(this.selectedMemberLimits);

        // Update the limit drop down for the member
        this.updateLimitsDropdownList(this.selectedMemberLimits);
        this.filterData.filterOptions[FILTER_OPTIONS_INDEX.LIMIT].filterDropdownData = this.dropdownLimits;

        // Update sub tables
        this.updateSubTables();
      }
    },

    updateSubTables() {
      // There are tables that may need to show additional tables
      // First check if the limit has consumptions only to be displayed
      const consumption = findItemWithAttribute(this.tableRefData.consumptionOnly, 'name', this.selectedFilter.limits);

      if (consumption != null) {
        // Need to map the modalities into the consumption data to be shown
        this.updateConsumptionTableData(consumption);
        this.modalityTable.showTable = true;
      } else {
        this.modalityTable.showTable = false;
      }

      // There may be tables that will need to show sub limits.
      const subLimits = findItemWithAttribute(this.tableRefData.subLimits, 'limitName', this.selectedFilter.limits);

      if (subLimits != null) {
        this.updateModalityAndSubLimitTableData(subLimits);

        // Set modality table colSetting for those with sub limits (Med Prescribed Devices & Apps)
        this.modalityTable.showTable = true;
        this.showSubLimitTable = true;
      } else {
        // Only hide the modality table if consumption also does not need to be shown
        if (consumption == null) {
          this.modalityTable.showTable = false;
        }
        this.showSubLimitTable = false;
      }

      // Need to determine whether the replacement period table should be shown
      const replacementPeriod = findItemWithAttribute(this.tableRefData.replacementPeriods, 'name', this.selectedFilter.limits);
      if (replacementPeriod != null) {
        this.updateReplacementData(replacementPeriod);
        this.replacementTable.showTable = true;
      } else {
        this.replacementTable.showTable = false;
      }

      // Update member dropdown for getting the product based on the as at date
      this.appliedFilters.member = this.selectedFilter.member;

      // Update result category label
      this.appliedFilters.limits = isEmptyString(this.selectedFilter.limits) ? DEFAULT_LIMIT : this.selectedFilter.limits;

      this.updateQuickNavLinks();
    },

    updateConsumptionTableData(consumptionInfo) {
      // Get the modalities data from the limit using the group name
      const modalities = findItemWithAttribute(this.selectedMemberLimits, 'name', consumptionInfo.name)?.modalities ?? [];

      // Set up the headings and need to complete in this manner, since some of the headings display will be blank, but will be required so that the layout of the table
      // matches that of designs.
      const headings = {};
      headings[consumptionInfo.tableTitle] = consumptionInfo.tableTitle;
      headings[CONSUMPTION_TABLE_COLUMNS.BLANK_ONE.key] = CONSUMPTION_TABLE_COLUMNS.BLANK_ONE.display;
      headings[CONSUMPTION_TABLE_COLUMNS.BLANK_TWO.key] = CONSUMPTION_TABLE_COLUMNS.BLANK_TWO.display;
      headings[CONSUMPTION_TABLE_COLUMNS.BLANK_THREE.key] = CONSUMPTION_TABLE_COLUMNS.BLANK_THREE.display;
      headings[CONSUMPTION_TABLE_COLUMNS.CONSUMPTION] = CONSUMPTION_TABLE_COLUMNS.CONSUMPTION;
      headings[CONSUMPTION_TABLE_COLUMNS.BLANK_FOUR.key] = CONSUMPTION_TABLE_COLUMNS.BLANK_FOUR.display;

      this.modalityTable.headings = headings;
      this.modalityTable.refData = this.referenceData[consumptionInfo.contentKey] ?? this.modalityTable.refData;

      const consumptionData = [];
      modalities.forEach((modalityItem) => {
        const rowRecord = {};

        rowRecord[consumptionInfo.tableTitle] = modalityItem.name;
        rowRecord[CONSUMPTION_TABLE_COLUMNS.BLANK_ONE.key] = CONSUMPTION_TABLE_COLUMNS.BLANK_ONE.display;
        rowRecord[CONSUMPTION_TABLE_COLUMNS.BLANK_TWO.key] = CONSUMPTION_TABLE_COLUMNS.BLANK_TWO.display;
        rowRecord[CONSUMPTION_TABLE_COLUMNS.BLANK_THREE.key] = CONSUMPTION_TABLE_COLUMNS.BLANK_THREE.display;
        rowRecord[CONSUMPTION_TABLE_COLUMNS.CONSUMPTION] = this.defaultValue(modalityItem.consumed, false, true);
        rowRecord[CONSUMPTION_TABLE_COLUMNS.BLANK_FOUR.key] = CONSUMPTION_TABLE_COLUMNS.BLANK_FOUR.display;

        consumptionData.push(rowRecord);
      });

      this.modalityTable.data = sortByObjectProperty(consumptionData, consumptionInfo.tableTitle, null, 'ascending');
    },

    updateModalityAndSubLimitTableData(subLimitInfo) {
      const modalities = findItemWithAttribute(this.selectedMemberLimits, 'name', subLimitInfo.limitName)?.modalities ?? [];

      // Setting up the headings for the modality for sub limits
      const tableHeadings = [subLimitInfo.tableTitle, ...Object.values(SUB_LIMIT_COLUMNS), BLANK_COLUMNS.BLANK_ONE.display];
      this.modalityTable.headings = tableHeadings;
      this.modalityTable.refData = this.referenceData[subLimitInfo.contentKey] ?? this.modalityTable.refData;

      // Start mapping the table data
      const modalityData = [];

      if (subLimitInfo.modalities == null) {
        throw new Error('Sub Sub limit modality information is incorrect from CRM');
      }

      // Flatten the entire structure of the modalities sub sub limits and check if the modality item is contained in there at all
      const allSubLimits = subLimitInfo.modalities
        .map((modalityItem) => {
          if (modalityItem.subSubLimits == null) {
            throw new Error(`Sub sub limit in modality ${modalityItem.name} is missing`);
          }
          return modalityItem.subSubLimits.map((subLimitItem) => {
            return subLimitItem.name;
          });
        })
        .flat();

      modalities.forEach((modalityItem) => {
        // For some of the modality data, if they exist in the sub sub limit information, it will need to removed from the list
        if (!allSubLimits.includes(modalityItem.name)) {
          const rowRecord = {};
          rowRecord[subLimitInfo.tableTitle] = modalityItem.name;
          rowRecord[SUB_LIMIT_COLUMNS.START_DATE] = this.defaultValue(modalityItem.startPeriod, true);
          rowRecord[SUB_LIMIT_COLUMNS.END_DATE] = this.defaultValue(modalityItem.endPeriod, true);
          rowRecord[SUB_LIMIT_COLUMNS.ANNUAL] = this.defaultValue(modalityItem.subLimitMaximum, false, true, modalityItem.unlimited);
          rowRecord[SUB_LIMIT_COLUMNS.CONSUMPTION] = this.defaultValue(modalityItem.consumed, false, true);
          rowRecord[BLANK_COLUMNS.BLANK_ONE.display] = BLANK_COLUMNS.BLANK_ONE.display;

          modalityData.push(rowRecord);
        }
      });

      this.modalityTable.data = sortByObjectProperty(modalityData, subLimitInfo.tableTitle, null, 'ascending');

      // The mapping of the sub limit data will need to occur as well, and since the structure allows for multiple sub sub limits
      // Will need to clear the table data for sub limit since it will be regenerated
      this.subLimitTable = [];
      subLimitInfo.modalities.forEach((modalityItem) => {
        const tableData = {
          noRecordsText: DEFAULT_NO_RECORDS_TEXT,
          colAlignments: TABLE_ALIGNMENTS,
          headings: [],
          data: [],
        };

        tableData.headings = [modalityItem.tableTitle, ...Object.values(SUB_LIMIT_COLUMNS), BLANK_COLUMNS.BLANK_ONE.display];
        tableData.refData = this.referenceData[modalityItem.contentKey] ?? DEFAULT_INFORMATION_TEXT;

        // Go through the sub limit items to generate the record rows
        const data = [];
        modalityItem.subSubLimits.forEach((subSubLimitItem) => {
          // Find the record with the sub sub limit from the modalities
          const subSubLimitModality = findItemWithAttribute(modalities, 'name', subSubLimitItem.name);

          if (subSubLimitModality != null) {
            const rowRecord = {};

            rowRecord[modalityItem.tableTitle] = subSubLimitModality.name;
            rowRecord[SUB_LIMIT_COLUMNS.START_DATE] = this.defaultValue(subSubLimitModality.startPeriod, true);
            rowRecord[SUB_LIMIT_COLUMNS.END_DATE] = this.defaultValue(subSubLimitModality.endPeriod, true);
            rowRecord[SUB_LIMIT_COLUMNS.ANNUAL] = this.defaultValue(subSubLimitModality.subLimitMaximum, false, true, subSubLimitModality.unlimited);
            rowRecord[SUB_LIMIT_COLUMNS.CONSUMPTION] = this.defaultValue(subSubLimitModality.consumed, false, true);
            rowRecord[BLANK_COLUMNS.BLANK_ONE.display] = BLANK_COLUMNS.BLANK_ONE.display;

            data.push(rowRecord);
          }
        });

        tableData.data = sortByObjectProperty(data, modalityItem.tableTitle, null, 'ascending');
        this.subLimitTable.push(tableData);
      });
    },

    updateReplacementData(replacementPeriodInfo) {
      if (this.replacementPeriods) {
        this.replacementTable.headings = [replacementPeriodInfo.tableTitle, ...Object.values(REPLACEMENT_COLUMNS)];

        const data = [];

        // Need to flatten out the replacement period table
        this.replacementPeriods.forEach((replacementPeriodItem) => {
          if (replacementPeriodItem.providerLimitRuleList == null) {
            throw new Error('The replacement periods are missing the provider limit rule list');
          }

          // For the moment only need to do Y = years.
          let replacementPeriod = `${replacementPeriodItem.replacementPeriod}`;
          if (replacementPeriodItem.replacementPeriodUom.toUpperCase() === 'Y') {
            replacementPeriod = `${replacementPeriod} year${replacementPeriodItem.replacementPeriod > 1 ? 's' : ''}`;
          }

          replacementPeriodItem.providerLimitRuleList.forEach((providerLimitRuleItem) => {
            const rowRecord = {};
            rowRecord[replacementPeriodInfo.tableTitle] = providerLimitRuleItem.descr;
            rowRecord[REPLACEMENT_COLUMNS.PERIOD] = replacementPeriod;
            data.push(rowRecord);
          });
        });

        this.replacementTable.data = sortByObjectProperty(data, replacementPeriodInfo.tableTitle, null, 'ascending');
      }
    },

    scrollTo(linkTo) {
      const element = this.$refs[linkTo];
      element.scrollIntoView({ behavior: 'smooth' });
    },

    checkSomeObjectPropertiesTrue,
    getResultsLabel,
  },

  validations: {
    currentAsOfDate: {
      isDateFormat(startDate) {
        if (startDate === '') {
          return true;
        }
        return isDateFormat(startDate);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
#calloutText {
  text-align: left;
}

.return-to-top {
  margin-top: spacer(2);
}

.limit-tables {
  margin-top: rem(80);

  :deep(.table-content) {
    h3 {
      margin-bottom: 0;
    }
  }

  :deep(table) {
    margin: 0;
  }
}

#replacement-period-table {
  position: relative;
  width: 100%;
  margin-top: rem(120);

  &::before {
    content: '';
    position: absolute;
    background-color: $LIGHT_GREEN_TINT;
    width: 100vw;
    height: 100%;
    padding: spacer(5) 0;
    top: -50px;
    left: calc(-50vw + 50%);
  }

  :deep(table) {
    background-color: $LIGHT_GREEN_TINT;
  }
}

svg {
  color: $LIGHT_GREEN_TINT;
  width: 30px;
  height: 30px;

  * {
    @include mobile {
      width: 26px;
      height: 26px;
      transform: scale(0.857); // Resize svg to 24px in mobile view
    }
  }
}
</style>
